import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ---------- Accounts (план счетов) ----------
export interface Account {
  id: string;
  code: string;
  name: string;
  description: string | null;
  account_type: 'A' | 'P' | 'AP';
  is_group_only: boolean;
  is_off_balance: boolean;
  use_currency: boolean;
  use_quantity: boolean;
  do_not_revaluate: boolean;
}

export interface AccountUpdate {
  name?: string;
  description?: string | null;
  do_not_revaluate?: boolean;
}

export const accounts = {
  list: (params?: { q?: string; chart_code?: string; only_postable?: boolean; off_balance?: boolean; limit?: number }) =>
    api.get<Account[]>('/accounts', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Account>(`/accounts/${id}`).then((r) => r.data),
  update: (id: string, payload: AccountUpdate) =>
    api.patch<Account>(`/accounts/${id}`, payload).then((r) => r.data),
};

// ---------- Manual operations (бухгалтерская справка) ----------
export interface ManualOperationEntry {
  id?: string;
  line_number: number;
  dt_account_id: string;
  dt_subconto_1_id?: string | null;
  dt_subconto_1_type?: string | null;
  dt_subconto_2_id?: string | null;
  dt_subconto_2_type?: string | null;
  dt_subconto_3_id?: string | null;
  dt_subconto_3_type?: string | null;
  dt_quantity?: string | null;
  dt_currency_id?: string | null;
  dt_currency_amount?: string | null;
  cr_account_id: string;
  cr_subconto_1_id?: string | null;
  cr_subconto_1_type?: string | null;
  cr_subconto_2_id?: string | null;
  cr_subconto_2_type?: string | null;
  cr_subconto_3_id?: string | null;
  cr_subconto_3_type?: string | null;
  cr_quantity?: string | null;
  cr_currency_id?: string | null;
  cr_currency_amount?: string | null;
  amount: string; // numeric — передаём строкой, чтобы не терять точность
  tax_amount?: string | null; // «Сума (н/о)» — податковий облік
  content?: string | null;
  project_id?: string | null;
}

// Типова операція — шаблон рядків бухдовідки (без сум/значень субконто).
export interface ManualOperationTemplate {
  id: string;
  name: string;
  content: string | null;
  entries: Partial<ManualOperationEntry>[];
}

export interface ManualOperationListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  total_amount?: string | null;
  project_id?: string | null;
  // Block 5.3f:
  state?: CancellableState;
}

export interface ManualOperation extends ManualOperationListItem {
  posted_at: string | null;
  comment: string | null;
  entries: ManualOperationEntry[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ManualOperationCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content?: string | null;
  comment?: string | null;
  entries: ManualOperationEntry[];
  project_id?: string | null;
  // Фаза 2.1 #3: subtype для специфічних flows (batch_correction).
  operation_subtype?: string | null;
  related_batch_key_id?: string | null;
  new_cost_price?: string | null;
}

export interface ManualOperationUpdate {
  document_number?: string;
  document_date?: string;
  operation_content?: string | null;
  comment?: string | null;
  entries?: ManualOperationEntry[];
  project_id?: string | null;
  operation_subtype?: string | null;
  related_batch_key_id?: string | null;
  new_cost_price?: string | null;
}

export const manualOps = {
  list: (params?: { organization_id?: string; posted?: boolean; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<ManualOperationListItem>>('/manual-operations', { params }).then((r) => r.data),
  get: (id: string) => api.get<ManualOperation>(`/manual-operations/${id}`).then((r) => r.data),
  create: (payload: ManualOperationCreate) =>
    api.post<ManualOperation>('/manual-operations', payload).then((r) => r.data),
  update: (id: string, payload: ManualOperationUpdate) =>
    api.patch<ManualOperation>(`/manual-operations/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/manual-operations/${id}`).then(() => undefined),
  post: (id: string) => api.post<ManualOperation>(`/manual-operations/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<ManualOperation>(`/manual-operations/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/manual-operations/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

export const manualOpTemplates = {
  list: () =>
    api.get<ManualOperationTemplate[]>('/manual-operation-templates').then((r) => r.data),
  upsert: (payload: { name: string; content?: string | null; entries: Partial<ManualOperationEntry>[] }) =>
    api.post<ManualOperationTemplate>('/manual-operation-templates', payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/manual-operation-templates/${id}`).then(() => undefined),
};

// ---------- Trial balance (ОСВ) ----------
// ч.1036: стандартні звіти — ОСВ / ОСВ по рахунку / картка / аналіз субконто.
export interface LedgerBalances {
  opening_dt: string; opening_cr: string;
  turnover_dt: string; turnover_cr: string;
  closing_dt: string; closing_cr: string;
  /** ч.1037: кількість — лише для кількісних рахунків і за запитом (with_quantity). */
  opening_qty?: string | null; turnover_qty_dt?: string | null;
  turnover_qty_cr?: string | null; closing_qty?: string | null;
}

export interface TrialBalanceRow extends LedgerBalances {
  account_code: string;
  account_name: string;
  account_type: string;
  parent_code: string | null;
  level: number;
  is_group: boolean;
  is_off_balance: boolean;
  has_subconto: boolean;
}

export interface TrialBalance {
  organization_id: string;
  date_from: string;
  date_to: string;
  expanded: boolean;
  with_quantity?: boolean;
  rows: TrialBalanceRow[];
  totals: LedgerBalances;
  off_balance_totals: LedgerBalances;
  balanced: boolean;
}

export interface AccountDetailRow extends LedgerBalances {
  kind: 'account' | 'subconto' | 'currency' | string;
  level: number;
  label: string;
  account_code: string | null;
  subconto_type: string | null;
  subconto_id: string | null;
  currency_code: string | null;
  is_group: boolean;
  opening_cur: string | null;
  turnover_dt_cur: string | null;
  turnover_cr_cur: string | null;
  closing_cur: string | null;
}

export interface AccountDetail {
  organization_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  use_currency: boolean;
  use_quantity?: boolean;
  with_quantity?: boolean;
  date_from: string;
  date_to: string;
  group_by: string[];
  rows: AccountDetailRow[];
  totals: LedgerBalances;
}

export interface GoodsBalanceRow {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_article: string | null;
  nomenclature_name: string;
  quantity: string;
  cost: string | null;
}

export interface GoodsBalance {
  organization_id: string;
  on_date: string;
  warehouse_id: string | null;
  rows: GoodsBalanceRow[];
  totals: { quantity: string; cost: string };
}

export interface GoodsBatchRow {
  warehouse_id: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_name: string;
  batch_key_id: string;
  receipt_document_type: string;
  receipt_document_id: string;
  receipt_date: string;
  cost_price: string;
  quantity: string;
  cost: string;
}

export interface GoodsBatches {
  organization_id: string;
  on_date: string;
  rows: GoodsBatchRow[];
}

export interface AccountCardRow {
  entry_id: string;
  entry_date: string;
  /** ч.1038: у картці субконто — рахунок сторони. */
  account_code?: string | null;
  document_type: string | null;
  document_id: string | null;
  document_number: string | null;
  document_date: string | null;
  side: 'DT' | 'CR' | string;
  corr_account_code: string;
  corr_account_name: string | null;
  analytics: string;
  corr_analytics: string;
  content: string | null;
  amount_dt: string;
  amount_cr: string;
  currency_code: string | null;
  currency_amount: string | null;
  quantity?: string | null;
  running_balance: string;
}

export interface AccountCard {
  organization_id: string;
  account_code: string | null;      // null — картка субконто (усі рахунки)
  account_name: string;
  date_from: string;
  date_to: string;
  subconto_id: string | null;
  corr_account_code: string | null;
  side?: 'DT' | 'CR' | null;
  rows: AccountCardRow[];
  truncated: boolean;
  limit: number;
  totals: LedgerBalances;
}

export interface SubcontoAnalysisRow extends LedgerBalances {
  kind: 'subconto' | 'account' | string;
  level: number;
  label: string;
  subconto_type: string;
  subconto_id: string | null;
  account_code: string | null;
  account_name: string | null;
  is_group: boolean;
}

export interface SubcontoAnalysis {
  organization_id: string;
  subconto_type: string;
  subconto_label: string;
  subconto_id: string | null;
  date_from: string;
  date_to: string;
  rows: SubcontoAnalysisRow[];
  totals: LedgerBalances;
}

export interface SubcontoTypeItem { type: string; label: string }

// ч.1038: аналіз рахунку, обороти рахунку, головна книга, шахматка.
export interface AccountAnalysisRow {
  corr_account_code: string; corr_account_name: string | null;
  turnover_dt: string; turnover_cr: string;
}
export interface AccountAnalysis {
  organization_id: string; account_code: string; account_name: string;
  date_from: string; date_to: string; rows: AccountAnalysisRow[]; totals: LedgerBalances;
}
export interface PeriodRow extends LedgerBalances {
  kind: 'account' | 'period' | 'corr' | string;
  level: number; label: string; account_code: string;
  period_start: string; period_end: string;
  corr_account_code: string | null; corr_account_name: string | null; is_group: boolean;
}
export interface AccountTurnover {
  organization_id: string; account_code: string; account_name: string;
  date_from: string; date_to: string; periodicity: string; rows: PeriodRow[]; totals: LedgerBalances;
}
export interface GeneralLedger {
  organization_id: string; date_from: string; date_to: string; periodicity: string; rows: PeriodRow[];
}
export interface ChessSheet {
  organization_id: string; date_from: string; date_to: string; level: string;
  dt_accounts: { code: string; name: string }[]; cr_accounts: { code: string; name: string }[];
  cells: { dt: string; cr: string; amount: string }[];
  row_totals: Record<string, string>; col_totals: Record<string, string>; total: string;
}

export interface ReconciliationRow {
  movement_date: string;
  document_type: string;
  document_id: string;
  document_number: string | null;
  direction: string;
  debit: string;
  credit: string;
  balance: string;
}

export interface Reconciliation {
  organization_id: string;
  contractor_id: string;
  contract_id: string | null;
  date_from: string;
  date_to: string;
  opening_balance: string;
  rows: ReconciliationRow[];
  closing_balance: string;
  turnover_debit: string;
  turnover_credit: string;
}

export interface ProductionWipRow {
  department_id: string | null;
  department_name: string | null;
  nomenclature_group_id: string | null;
  nomenclature_group_name: string | null;
  balance: string;
}
export interface ProductionWipReport {
  as_of: string;
  rows: ProductionWipRow[];
  total: string;
}

export interface SettlementByDocumentRow {
  settlement_doc_type: string;
  settlement_doc_id: string;
  document_number: string | null;
  document_date: string | null;
  contractor_id: string;
  contractor_name: string | null;
  /** ч.1039: null — борг без договору (довідка, залишки). */
  contract_id: string | null;
  contract_name: string | null;
  total_amount: string;
  paid_amount: string;
  open_amount: string;
  due_date: string | null;
  overdue_days: number;
}

export interface SettlementsByDocumentsOut {
  items: SettlementByDocumentRow[];
  total: number;
  limit: number;
  offset: number;
  total_open: string;
  total_overdue: string;
  /** ч.1039: відкриті аванси сторони (оплати/довідки без документа-боргу). */
  total_advances: string;
  /** ч.1039: сальдо рахунків розрахунків по проводках БЕЗ контрагента. */
  total_unattributed: string;
}

export interface LedgerParams {
  organization_id: string;
  date_from: string;   // YYYY-MM-DD, включно
  date_to: string;     // YYYY-MM-DD, включно
  chart_code?: string;
}

/** Ім'я файлу з Content-Disposition (спершу filename*=UTF-8''…, інакше filename=). */
export function filenameFromDisposition(h: string | undefined, fallback = 'report.xlsx'): string {
  if (!h) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(h);
  if (star?.[1]) { try { return decodeURIComponent(star[1]); } catch { /* ignore */ } }
  const plain = /filename="?([^";]+)"?/i.exec(h);
  return plain?.[1] ?? fallback;
}

export const reports = {
  // П5 (2026-07-24): розрахунки по документах (FIFO-закриття, як в 1С).
  settlementsByDocuments: (params: {
    organization_id: string;
    contractor_id?: string;
    contract_id?: string;
    side?: 'receivable' | 'payable';
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) => api.get<SettlementsByDocumentsOut>('/reports/settlements-by-documents', { params }).then((r) => r.data),
  // ч.1036: дати — включно (YYYY-MM-DD), як бачить їх бухгалтер.
  trialBalance: (params: LedgerParams & { expanded?: boolean; with_quantity?: boolean }) =>
    api.get<TrialBalance>('/reports/trial-balance', { params }).then((r) => r.data),
  trialBalanceAccount: (params: LedgerParams & { account_code: string; group_by?: string; with_quantity?: boolean }) =>
    api.get<AccountDetail>('/reports/trial-balance/account', { params }).then((r) => r.data),
  productionWip: (params: {
    organization_id: string;
    as_of?: string;
  }) => api.get<ProductionWipReport>('/reports/production-wip', { params }).then((r) => r.data),
  goodsBalance: (params: {
    organization_id: string;
    on_date: string;
    warehouse_id?: string;
  }) => api.get<GoodsBalance>('/reports/goods-balance', { params }).then((r) => r.data),
  goodsBatches: (params: {
    organization_id: string;
    on_date: string;
  }) => api.get<GoodsBatches>('/reports/goods-batches-balance', { params }).then((r) => r.data),
  accountCard: (params: LedgerParams & {
    account_code?: string; subconto_id?: string; corr_account_code?: string; side?: 'DT' | 'CR';
  }) => api.get<AccountCard>('/reports/account-card', { params }).then((r) => r.data),
  accountAnalysis: (params: LedgerParams & { account_code: string }) =>
    api.get<AccountAnalysis>('/reports/account-analysis', { params }).then((r) => r.data),
  accountTurnover: (params: LedgerParams & { account_code: string; periodicity?: string }) =>
    api.get<AccountTurnover>('/reports/account-turnover', { params }).then((r) => r.data),
  generalLedger: (params: LedgerParams & { periodicity?: string }) =>
    api.get<GeneralLedger>('/reports/general-ledger', { params }).then((r) => r.data),
  chessSheet: (params: LedgerParams & { level?: string }) =>
    api.get<ChessSheet>('/reports/chess-sheet', { params }).then((r) => r.data),
  subcontoTypes: (organization_id: string) =>
    api.get<{ items: SubcontoTypeItem[] }>('/reports/subconto-types', { params: { organization_id } })
      .then((r) => r.data.items),
  subcontoAnalysis: (params: LedgerParams & { subconto_type: string; subconto_id?: string }) =>
    api.get<SubcontoAnalysis>('/reports/subconto-analysis', { params }).then((r) => r.data),
  /** Той самий звіт файлом Excel (бекенд віддає .xlsx з тих самих даних). */
  ledgerXlsx: (path: string, params: Record<string, string | boolean | undefined>) =>
    api.get<Blob>(path, { params: { ...params, format: 'xlsx' }, responseType: 'blob' })
      .then((r) => ({ blob: r.data, filename: filenameFromDisposition(r.headers?.['content-disposition']) })),
  vatSummary: (params: {
    organization_id: string;
    period_year: number;
    period_month: number;
  }) => api.get<VatSummary>('/reports/vat-summary', { params }).then((r) => r.data),
  goodsReserved: (params: {
    organization_id: string;
    on_date: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) => api.get<GoodsReserved>('/reports/goods-reserved', { params }).then((r) => r.data),
  // ПРОДАЖ-розбивка Етап 4 (2026-06-03): агрегована аналітика 4 показника.
  // 2026-08-22: серверна пагінація/сортування + totals по всій вибірці.
  inventoryAnalytics: (params: {
    organization_id: string;
    on_date: string;
    warehouse_id?: string;
    q?: string;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) => api.get<InventoryAnalytics>('/reports/inventory-analytics', { params }).then((r) => r.data),
  // ч.918: «можна продати» для рахунку покупцю — склад + в дорозі − резерв
  // інших − замовлено іншими (по організації, без поточного документа).
  sellableBalance: (params: {
    organization_id: string; on_date: string; nomenclature_ids?: string;
    exclude_order_id?: string;
  }) => api.get<{ rows: SellableBalanceRow[] }>('/reports/sellable-balance', { params }).then((r) => r.data),
  // ч.920: рядки попапа підбору (рахунок/замовлення покупця) — серверна
  // пагінація/сортування, показники наявності, ціни за типами.
  sellablePicker: (params: {
    organization_id: string; on_date: string; warehouse_id?: string; kind_id?: string;
    only_kind?: 'goods' | 'services'; q?: string;
    with_stock?: boolean; with_transit?: boolean; with_reserved?: boolean; with_ordered?: boolean;
    price_type_ids?: string; sort_by?: string; sort_dir?: 'asc' | 'desc';
    limit?: number; offset?: number;
  }) => api.get<{ items: SellablePickerRow[]; total: number }>('/reports/sellable-picker', { params }).then((r) => r.data),
  reconciliationAct: (params: {
    organization_id: string;
    contractor_id: string;
    contract_id?: string;
    date_from: string;
    date_to: string;
  }) => api.get<Reconciliation>('/reports/reconciliation-act', { params }).then((r) => r.data),
  contractorBalance: (params: {
    organization_id: string;
    contractor_id: string;
    contract_id: string;
    account_code: string;
    on_date?: string;
  }) => api.get<{ debit: string; credit: string; balance_signed: string }>(
    '/reports/contractor-balance', { params }
  ).then((r) => r.data),
  vatDeclarationXml: async (params: {
    organization_id: string;
    period_year: number;
    period_month: number;
  }): Promise<Blob> => {
    const r = await api.get('/reports/vat-declaration.xml', {
      params,
      responseType: 'blob',
    });
    return r.data as Blob;
  },
};

export interface VatSummaryRateRow {
  rate_code: string;
  rate_label: string;
  base: string;
  vat: string;
}

export interface VatSummary {
  organization_id: string;
  period_year: number;
  period_month: number;
  obligations: VatSummaryRateRow[];
  credit: VatSummaryRateRow[];
  total_obligations: string;
  total_credit: string;
  to_pay: string;
  to_refund: string;
}

export interface GoodsReservedRow {
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_name: string;
  customer_order_id: string;
  customer_order_number: string;
  customer_order_date: string;
  contractor_id: string;
  contractor_name: string;
  quantity: string;
}

export interface GoodsReserved {
  organization_id: string;
  on_date: string;
  rows: GoodsReservedRow[];
  total: number;
  total_quantity: string;
  limit: number;
  offset: number;
}

// ПРОДАЖ-розбивка Етап 4 (2026-06-03): агрегована аналітика наявності.
export interface SellablePickerRow {
  warehouse_id: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  nomenclature_id: string;
  nomenclature_code: string | null;
  nomenclature_article: string | null;
  nomenclature_name: string;
  uom_short: string | null;
  is_service: boolean;
  quantity: string;    // на складі рядка
  in_transit: string;  // по організації
  reserved: string;    // на складі рядка
  ordered: string;     // по організації
  balance: string;     // на складах + в дорозі − в резерві − замовлено
  prices: Record<string, string | null>;  // price_type_id → ціна
}

export interface SellableBalanceRow {
  nomenclature_id: string;
  stock: string;       // на складах
  reserved: string;    // у резерві під рахунки інших покупців
  reserved_by_warehouse: Record<string, string>;  // той самий резерв по складах
  ordered: string;     // замовлено іншими без резерву
  in_transit: string;  // в дорозі від постачальників
  free_now: string;    // stock − reserved
  sellable: string;    // stock + in_transit − reserved − ordered
}

export interface InventoryAnalyticsRow {
  warehouse_id: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  nomenclature_id: string;
  nomenclature_code: string;
  nomenclature_name: string;
  uom_short: string | null;
  stock: string;       // Залишок
  reserved: string;    // Резерв
  in_transit: string;  // В дорозі
  demanded: string;    // Попит
  available: string;   // Залишок - Резерв
}

/** Підсумки аналітики наявності — з сервера по ВСІЙ вибірці (не по сторінці). */
export interface InventoryAnalyticsTotals {
  stock: string;
  reserved: string;
  in_transit: string;
  demanded: string;
  available: string;
}

export interface InventoryAnalytics {
  organization_id: string;
  on_date: string;
  rows: InventoryAnalyticsRow[];
  // 2026-08-22: конверт серверної пагінації.
  total: number;
  limit: number;
  offset: number;
  totals: InventoryAnalyticsTotals;
}
